// Station setup (L6): which of Manufacturing's work centers have shop-floor stations.
import { useState } from 'react';

import type { WorkCenterResponse } from '../../api/generated/WorkCenterResponse';
import { useSetStations, useWorkCenters } from '../../api/queries';
import { canBuild, useSession } from '../../auth/session';
import { Button, ErrorPanel, Loading, PageHeader } from '../../design/components';

export function StationsPage() {
    const centers = useWorkCenters();
    return (
        <section className="sf-stack" aria-labelledby="stations-heading">
            <PageHeader
                id="stations-heading"
                title="Stations"
                description="The names come from Manufacturing’s list of work centers."
            />
            {centers.isPending ? <Loading what="the work centers" /> : null}
            {centers.isError ? (
                <ErrorPanel error={centers.error} title="Manufacturing’s work centers couldn’t be loaded." />
            ) : null}
            {centers.data ? (
                <StationChoice
                    key={centers.data.map((center) => `${center.name}:${String(center.hasStation)}`).join('|')}
                    centers={centers.data}
                />
            ) : null}
        </section>
    );
}

/** The choice, starting from what is saved; it remounts when the saved stations change. */
function StationChoice({ centers }: { centers: readonly WorkCenterResponse[] }) {
    const session = useSession();
    const save = useSetStations();
    const [chosen, setChosen] = useState<ReadonlySet<string>>(
        () => new Set(centers.filter((center) => center.hasStation).map((center) => center.name)),
    );
    const canAct = canBuild(session);
    return (
        <>
            <fieldset className="sf-card sf-stack" disabled={!canAct}>
                <legend>Work centers with a station</legend>
                {centers.map((center) => (
                    <label key={center.name} className="sf-row" style={{ minHeight: 'var(--sf-touch)' }}>
                        <input
                            type="checkbox"
                            checked={chosen.has(center.name)}
                            onChange={(event) => {
                                const next = new Set(chosen);
                                if (event.target.checked) next.add(center.name);
                                else next.delete(center.name);
                                setChosen(next);
                            }}
                        />
                        {center.name}
                    </label>
                ))}
            </fieldset>
            {canAct ? (
                <div className="sf-row">
                    <Button
                        variant="primary"
                        disabled={save.isPending}
                        onClick={() => {
                            save.mutate([...chosen]);
                        }}
                    >
                        Save stations
                    </Button>
                    {save.isSuccess ? <span role="status">Saved.</span> : null}
                </div>
            ) : null}
            {save.isError ? <ErrorPanel error={save.error} /> : null}
        </>
    );
}
